/**
 * Copyright 2017 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import { LOGOUT_USER } from "openstack-uicore-foundation/lib/security/actions";
import { DEFAULT_PER_PAGE } from "../utils/constants";
import {
  ACCESS_REQUEST_ADDED,
  RECEIVE_ALL_SHOWS,
  RECEIVE_ALL_SPONSORS,
  REQUEST_ALL_SHOWS
} from "../actions/show-actions";

const DEFAULT_STATE = {
  allShows: [],
  allSponsors: [],
  showsCurrentPage: 1,
  showsLastPage: 1,
  showsSearchTerm: "",
  sponsorsCurrentPage: 1,
  perPage: DEFAULT_PER_PAGE
};

const requestAccessReducer = (state = DEFAULT_STATE, action) => {
  const { type, payload } = action;

  switch (type) {
    case LOGOUT_USER:
      return DEFAULT_STATE;
    case REQUEST_ALL_SHOWS: {
      const { term, page } = payload;
      return {
        ...state,
        showsSearchTerm: term,
        showsCurrentPage: page
      };
    }
    case RECEIVE_ALL_SHOWS: {
      const {
        data,
        current_page: showsCurrentPage,
        last_page: showsLastPage
      } = payload.response;

      const allShows =
        showsCurrentPage === 1 ? data : [...state.allShows, ...data];

      return {
        ...state,
        allShows,
        showsCurrentPage,
        showsLastPage
      };
    }
    case RECEIVE_ALL_SPONSORS: {
      const { data, current_page: sponsorsCurrentPage } = payload.response;

      /*
      payload structure
      {
        company: { id, name },
        sponsorships: [
          { type: { type: { id, name } } },
          ...
        ]
      }
       */

      //  this is an infinite scroll / "Load more" UX, we must append results from
      // the new page to what we already have in state.
      //
      // Important: we CANNOT mutate state.allSponsors directly (Redux immutability),
      // so when currentPage > 1 we create a shallow copy of tiers + sponsors arrays.
      const initialRes =
        sponsorsCurrentPage === 1
          ? []
          : state.allSponsors.map((tier) => ({
              ...tier,
              sponsors: [...tier.sponsors]
            }));

      const allSponsors = data.reduce((res, it) => {
        const company = { id: it.company.id, name: it.company.name };

        const sponsorships = it.sponsorships ?? [];

        sponsorships.forEach((sp) => {
          const tierType = sp?.type?.type; // <- new tier path
          if (!tierType?.id) return;

          let foundTier = res.find((t) => t.id === tierType.id);

          // Create the tier bucket if this is the first time we see this tier id
          if (!foundTier) {
            foundTier = {
              id: tierType.id,
              name: tierType.name,
              sponsors: []
            };
            res.push(foundTier);
          }

          // Avoid duplicates when:
          // - the same company appears multiple times in the same tier (within a page),
          // - or when "load more" returns an overlapping company/tier pair across pages.
          const alreadyInTier = foundTier.sponsors.some(
            (s) => s.id === company.id
          );
          if (!alreadyInTier) {
            foundTier.sponsors.push(company);
          }
        });

        return res;
      }, initialRes);

      return {
        ...state,
        allSponsors,
        sponsorsCurrentPage
      };
    }
    case ACCESS_REQUEST_ADDED: {
      // reset when request added
      return {
        ...DEFAULT_STATE
      };
    }
    default:
      return state;
  }
};

export default requestAccessReducer;
